/** §2.5 slot-keyed DOM recycling. Pure, allocation-light. */
import { GRID_POOL_MAX } from '@/config/constants';

export type SlotAssignment = {
  /** slot → itemIndex or -1 */
  slots: Int32Array;
  /** itemIndex → slot (Map, only for bound items) */
  bySlotOf: Map<number, number>;
  changed: boolean;
};

export function poolSizeFor(viewportH: number, colWidth: number, footer: number, cols: number): number {
  const minCardH = colWidth / 5 + footer;
  const n = Math.ceil((3 * viewportH) / Math.max(1, minCardH)) * cols + cols;
  return Math.max(cols, Math.min(GRID_POOL_MAX, n));
}

export class SlotPool {
  slots: Int32Array;
  private itemToSlot = new Map<number, number>();
  private free: number[] = [];
  private wantedSet = new Set<number>();

  constructor(size: number) {
    this.slots = new Int32Array(size).fill(-1);
    for (let s = size - 1; s >= 0; s--) this.free.push(s);
  }

  get size(): number {
    return this.slots.length;
  }

  slotOf(itemIndex: number): number | undefined {
    return this.itemToSlot.get(itemIndex);
  }

  /** Resize pool (e.g. relayout changed cols/viewport); keeps existing bindings that fit. */
  resize(size: number): void {
    if (size === this.slots.length) return;
    const next = new Int32Array(size).fill(-1);
    const n = Math.min(size, this.slots.length);
    for (let s = 0; s < n; s++) next[s] = this.slots[s]!;
    for (let s = n; s < this.slots.length; s++) {
      const it = this.slots[s]!;
      if (it >= 0) this.itemToSlot.delete(it);
    }
    this.slots = next;
    this.free = [];
    for (let s = size - 1; s >= 0; s--) if (next[s] === -1) this.free.push(s);
  }

  /**
   * Items changed non-incrementally: keep slot identity for items that still exist by mapping
   * old item indices to new ones (−1 drops the binding). Keeps thumbnails mounted across
   * in-place card updates and deletions elsewhere in the list.
   */
  remap(fn: (oldIndex: number) => number): void {
    this.itemToSlot.clear();
    this.free = [];
    for (let s = this.slots.length - 1; s >= 0; s--) {
      const it = this.slots[s]!;
      const ni = it >= 0 ? fn(it) : -1;
      if (ni >= 0 && !this.itemToSlot.has(ni)) {
        this.slots[s] = ni;
        this.itemToSlot.set(ni, s);
      } else {
        this.slots[s] = -1;
        this.free.push(s);
      }
    }
  }

  /** Forget all bindings (items changed non-incrementally). */
  reset(): void {
    this.slots.fill(-1);
    this.itemToSlot.clear();
    this.free = [];
    for (let s = this.slots.length - 1; s >= 0; s--) this.free.push(s);
  }

  /**
   * Bind `wanted` item indices to slots. Keeps slots whose item is still wanted, frees the rest,
   * assigns remaining wanted to free slots, grows by 25% if needed. Returns true if any
   * binding changed.
   */
  assign(wanted: readonly number[]): boolean {
    const wantedSet = this.wantedSet;
    wantedSet.clear();
    for (const i of wanted) wantedSet.add(i);
    let changed = false;
    // free slots no longer wanted
    for (let s = 0; s < this.slots.length; s++) {
      const it = this.slots[s]!;
      if (it >= 0 && !wantedSet.has(it)) {
        this.slots[s] = -1;
        this.itemToSlot.delete(it);
        this.free.push(s);
        changed = true;
      }
    }
    // assign missing
    for (const i of wanted) {
      if (this.itemToSlot.has(i)) continue;
      let s = this.free.pop();
      if (s === undefined) {
        const grow = Math.max(1, Math.ceil(this.slots.length * 0.25));
        const old = this.slots;
        this.slots = new Int32Array(old.length + grow).fill(-1);
        this.slots.set(old);
        for (let k = old.length + grow - 1; k >= old.length; k--) this.free.push(k);
        if (import.meta.env?.DEV) console.debug(`[grid] slot pool grown to ${this.slots.length}`);
        s = this.free.pop()!;
      }
      this.slots[s] = i;
      this.itemToSlot.set(i, s);
      changed = true;
    }
    return changed;
  }
}
